import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Beranda', path: '/', icon: 'fas fa-home', color: 'from-blue-500 to-blue-600' },
    { name: 'Timeline', path: '/timeline', icon: 'fas fa-calendar-alt', color: 'from-green-500 to-green-600' },
    { name: 'Overview', path: '/overview', icon: 'fas fa-chart-line', color: 'from-purple-500 to-purple-600' },
    {
      name: 'Manfaat & Fitur',
      icon: 'fas fa-star',
      color: 'from-yellow-500 to-orange-500',
      dropdown: [
        { name: 'Manfaat Program', path: '/benefits', icon: 'fas fa-star', description: 'Keuntungan digitalisasi sekolah' },
        { name: 'Manfaat iPad', path: '/ipad-benefits', icon: 'fas fa-tablet-alt', description: 'Fitur dan manfaat iPad untuk pembelajaran' },
        { name: 'iPad: Lebih dari Buku', path: '/ipad-beyond-books', icon: 'fas fa-rocket', description: 'Mengapa iPad bukan sekadar pengganti buku' },
        { name: 'Apa itu MDM?', path: '/mdm-explained', icon: 'fas fa-mobile-alt', description: 'Mobile Device Management explained' },
      ]
    },
    {
      name: 'Panduan & Checklist',
      icon: 'fas fa-clipboard-list',
      color: 'from-indigo-500 to-purple-600',
      dropdown: [
        { name: 'Checklist Pengasuhan', path: '/pengasuhan-checklist', icon: 'fas fa-clipboard-check', description: 'Panduan lengkap untuk orang tua' },
        { name: 'Tahapan Implementasi', path: '/implementation-phases', icon: 'fas fa-project-diagram', description: 'Langkah-langkah implementasi' },
        { name: 'What-Why-How', path: '/roadmap-what-why-how', icon: 'fas fa-question-circle', description: 'Konsep dasar roadmap digitalisasi' },
        { name: 'Keputusan Strategis', path: '/strategic-decisions', icon: 'fas fa-chess', description: 'Pertimbangan strategis penting' },
        { name: 'Distribusi iPad', path: '/ipad-distribution', icon: 'fas fa-tablet-alt', description: 'Alur dan mekanisme distribusi iPad' },
      ]
    }
  ];

  const isActive = (path) => location.pathname === path;

  const isDropdownActive = (dropdown) => {
    return dropdown.some(item => location.pathname === item.path);
  };

  const toggleDropdown = (index) => {
    setDropdownOpen(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setDropdownOpen({});
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-xl border-b border-gray-200/20' 
        : 'bg-white/90 backdrop-blur-sm shadow-lg'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          {/* Logo Section */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-4 group">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:scale-105">
                  <i className="fas fa-graduation-cap text-white text-xl"></i>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse"></div>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Al Wafi
                </h1>
                <p className="text-sm text-gray-500 font-medium">Digital Transformation</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2">
            {navItems.map((item, index) => (
              item.dropdown ? (
                <div 
                  key={index} 
                  className="relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => toggleDropdown(index)}
                    className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center space-x-3 group relative overflow-hidden ${
                      isDropdownActive(item.dropdown)
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105'
                        : 'text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-600 hover:shadow-lg hover:transform hover:scale-105'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${item.color} opacity-75`}></div>
                    <i className={item.icon}></i>
                    <span>{item.name}</span>
                    <i className={`fas fa-chevron-down text-xs transition-transform duration-300 ${dropdownOpen[index] ? 'rotate-180' : ''}`}></i>
                  </button>
                  
                  {dropdownOpen[index] && (
                    <div className="absolute top-full left-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 py-3 z-50 transform animate-in slide-in-from-top-2 duration-200">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-800 flex items-center space-x-2">
                          <i className={item.icon}></i>
                          <span>{item.name}</span>
                        </h3>
                      </div>
                      {item.dropdown.map((dropdownItem, dropdownIndex) => (
                        <Link
                          key={dropdownIndex}
                          to={dropdownItem.path}
                          onClick={() => setDropdownOpen({})}
                          className={`block px-4 py-4 text-sm transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 group ${
                            isActive(dropdownItem.path)
                              ? 'bg-gradient-to-r from-blue-50 to-purple-50 border-r-4 border-blue-500'
                              : ''
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${item.color} flex items-center justify-center text-white text-xs group-hover:scale-110 transition-transform duration-200`}>
                              <i className={dropdownItem.icon}></i>
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-800 group-hover:text-blue-600">
                                {dropdownItem.name}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {dropdownItem.description}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={index}
                  to={item.path}
                  className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center space-x-3 group relative overflow-hidden ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-600 hover:shadow-lg hover:transform hover:scale-105'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${item.color} opacity-75`}></div>
                  <i className={item.icon}></i>
                  <span>{item.name}</span>
                </Link>
              )
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-blue-600 focus:outline-none focus:text-blue-600 p-3 rounded-xl hover:bg-blue-50 transition-all duration-200"
            >
              <i className={`fas ${isOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden border-t border-gray-100">
            <div className="px-4 py-6 space-y-3 bg-gradient-to-br from-gray-50 to-blue-50/30">
              {navItems.map((item, index) => (
                item.dropdown ? (
                  <div key={index} className="space-y-2">
                    <button
                      onClick={() => toggleDropdown(`mobile-${index}`)}
                      className="w-full text-left px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 flex items-center justify-between bg-white shadow-sm hover:shadow-md border border-gray-100 hover:border-blue-200"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${item.color} flex items-center justify-center text-white text-sm`}>
                          <i className={item.icon}></i>
                        </div>
                        <span className="text-gray-800">{item.name}</span>
                      </div>
                      <i className={`fas fa-chevron-down text-sm text-gray-400 transition-transform duration-300 ${dropdownOpen[`mobile-${index}`] ? 'rotate-180' : ''}`}></i>
                    </button>
                    {dropdownOpen[`mobile-${index}`] && (
                      <div className="ml-4 space-y-2 animate-in slide-in-from-top-2 duration-200">
                        {item.dropdown.map((dropdownItem, dropdownIndex) => (
                          <Link
                            key={dropdownIndex}
                            to={dropdownItem.path}
                            onClick={() => setIsOpen(false)}
                            className={`block px-4 py-3 rounded-lg text-sm transition-all duration-200 border ${
                              isActive(dropdownItem.path)
                                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white border-blue-300 shadow-md'
                                : 'text-gray-700 hover:text-blue-600 bg-white hover:bg-blue-50 border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <i className={dropdownItem.icon}></i>
                              <div>
                                <div className="font-medium">{dropdownItem.name}</div>
                                <div className={`text-xs mt-1 ${isActive(dropdownItem.path) ? 'text-blue-100' : 'text-gray-500'}`}>
                                  {dropdownItem.description}
                                </div>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={index}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 flex items-center space-x-4 border ${
                      isActive(item.path)
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white border-blue-300 shadow-lg'
                        : 'text-gray-800 hover:text-blue-600 bg-white hover:bg-blue-50 border-gray-100 hover:border-blue-200 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${item.color} flex items-center justify-center text-white text-sm`}>
                      <i className={item.icon}></i>
                    </div>
                    <span>{item.name}</span>
                  </Link>
                )
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
